import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import AuthShell from "@/pages/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/Loading";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info } from "lucide-react";
import { toast } from "sonner";

const PW_HINT = "كلمة مرور حسابك على المنصة — اختر كلمة مرور خاصة بهذا الحساب، ولا تستخدم كلمة مرور بريدك الإلكتروني.";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", confirm: "", role: "student", gender: "male",
    phone: "", unit_id: "", student_number: "", university_status: "", specialization: "", admin_code: "",
  });
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/public/units").then((r) => setUnits(r.data)).catch(() => {}); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) { setError("كلمة المرور وتأكيدها غير متطابقين"); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.confirm;
      const u = await register(payload);
      toast.success("تم إنشاء الحساب بنجاح");
      nav("/app");
      if (u?.role === "pending_supervisor") toast.info("طلب الإشراف قيد المراجعة");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      title="إنشاء حساب"
      subtitle="انضم إلى منصة سُفراء"
      footer={<>لديك حساب؟ <Link to="/login" className="font-semibold text-greenx">تسجيل الدخول</Link></>}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-start">نوع الحساب</Label>
          <Select value={form.role} onValueChange={set("role")}>
            <SelectTrigger data-testid="reg-role" className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="student">طالب</SelectItem>
              <SelectItem value="teacher">مدرس / محاضر</SelectItem>
              <SelectItem value="supervisor">مشرف</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-start">الاسم الكامل</Label>
          <Input data-testid="reg-name" value={form.name} onChange={(e) => set("name")(e.target.value)} required className="mt-1.5 rounded-lg" />
        </div>
        <div>
          <Label className="text-start">البريد الإلكتروني</Label>
          <Input data-testid="reg-email" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} required className="mt-1.5 rounded-lg" />
        </div>
        <div>
          <Label className="text-start">رقم الهاتف</Label>
          <Input data-testid="reg-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} required className="mt-1.5 rounded-lg" placeholder="05xxxxxxxx" />
        </div>

        <div>
          <Label className="text-start">كلمة مرور حسابك على المنصة</Label>
          <Input data-testid="reg-password" type="password" value={form.password} onChange={(e) => set("password")(e.target.value)} required className="mt-1.5 rounded-lg" />
          <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-xs leading-relaxed text-amber-800">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {PW_HINT}
          </p>
        </div>
        <div>
          <Label className="text-start">تأكيد كلمة المرور</Label>
          <Input data-testid="reg-confirm" type="password" value={form.confirm} onChange={(e) => set("confirm")(e.target.value)} required className="mt-1.5 rounded-lg" />
        </div>

        {form.role === "student" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-start">الجنس</Label>
              <Select value={form.gender} onValueChange={set("gender")}>
                <SelectTrigger data-testid="reg-gender" className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="male">ذكر</SelectItem><SelectItem value="female">أنثى</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-start">الوحدة السكنية</Label>
              <Select value={form.unit_id} onValueChange={set("unit_id")}>
                <SelectTrigger data-testid="reg-unit" className="mt-1.5 rounded-lg"><SelectValue placeholder="اختر" /></SelectTrigger>
                <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-start">الرقم الجامعي / التعريفي (اختياري)</Label>
              <Input data-testid="reg-student-number" value={form.student_number} onChange={(e) => set("student_number")(e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
          </div>
        )}

        {form.role === "teacher" && (
          <div>
            <Label className="text-start">التخصص</Label>
            <Input data-testid="reg-spec" value={form.specialization} onChange={(e) => set("specialization")(e.target.value)} className="mt-1.5 rounded-lg" placeholder="مثال: علوم شرعية" />
          </div>
        )}

        {form.role === "supervisor" && (
          <div>
            <Label className="text-start">كلمة مرور الإدارة</Label>
            <Input data-testid="reg-admin-code" type="password" value={form.admin_code} onChange={(e) => set("admin_code")(e.target.value)} required className="mt-1.5 rounded-lg" />
            <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-slate-100 p-2 text-xs leading-relaxed text-slate-600">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> كلمة مرور الإدارة مخصصة للتحقق من صلاحية طلب الإشراف، وليست كلمة مرور حسابك. سيراجع المشرف العام طلبك قبل منح الصلاحيات.
            </p>
          </div>
        )}

        {error && <p data-testid="reg-error" className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>}
        <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full rounded-lg bg-navy py-6 hover:bg-navy-light active:scale-[0.98]">
          {loading ? <Spinner className="h-5 w-5 border-white/40 border-t-white" /> : "إنشاء الحساب"}
        </Button>
      </form>
    </AuthShell>
  );
}
